/**
 * An account's history as the store holds it (g110): what a screen reads to
 * show who changed what. The store composes this; a screen never sees it
 * apart from the store.
 *
 * It reads only through the account's wire, under `/api/sync`, and keeps
 * what it reads through the mirror, under the rules in `history.ts`. There is
 * no timer: a series is brought up to date when it is opened and whenever a
 * sync reply moves the stamp while it is on screen.
 */

import type { ActionDetailView, ActionSummary, ActivityStamp, CoverageView } from "../../api/src/shared/activity.js";
import { ApiError } from "../lib/http.js";
import { forgetHistory, keepHistory, readDetail, readHistory } from "./mirror.js";
import type { Pending } from "./queue.js";
import type { HistoryWire } from "./transport.js";
import {
  HEAD_READ_PAGES, advance, bounded, current, detailBytes, extend, freshSeries, meet, replaced, targetKey, targetQuery,
  type HistoryTarget, type SeriesRow,
} from "./history.js";

/**
 * Where a series stands. `unavailable`: the server does not let history be
 * read, or has not said it does. `loading`, `offline`, `signed-out`, `error`
 * and `not_found` are for a series with nothing held; once something is
 * held it is `ready`, and a refresh that fails says so in `failure` beside
 * what is shown.
 */
export type HistoryStatus = "unavailable" | "loading" | "ready" | "offline" | "signed-out" | "error" | "not_found";
export type HistoryFailure = "offline" | "signed-out" | "error" | "not_found";

export interface HistoryView {
  status: HistoryStatus;
  /** Newest first. */
  actions: readonly ActionSummary[];
  /** Older actions can be read. */
  more: boolean;
  loadingMore: boolean;
  /** A read is on its way for a series already shown. */
  refreshing: boolean;
  coverage: readonly CoverageView[] | null;
  /** When the server last answered for this series. */
  readAt: number | null;
  /** The series was read under the stamp the device holds now. */
  current: boolean;
  /** Why the last read for this series failed, while what is held is still shown. */
  failure: HistoryFailure | null;
  /** Changes this device has made that history does not hold yet, or that the server refused. */
  local: readonly LocalLine[];
}

/** One of this device's own changes, as the queue holds it or as the server refused it. */
export interface LocalLine {
  state: "waiting" | "refused";
  operationId: string | null;
  op: Pending["op"];
  entity: Pending["entity"];
  id: string;
  listId: string | null;
  fields: Pending["fields"];
  before: Pending["before"];
  occurredAt: number | null;
  reason?: string;
}

export interface DetailView {
  status: "loading" | "ready" | "offline" | "signed-out" | "error" | "not_found";
  view: ActionDetailView | null;
  loadingMore: boolean;
  failure: HistoryFailure | null;
}

export interface HistoryControl {
  available(): boolean;
  onAvailable(fn: (available: boolean) => void): () => void;
  view(target: HistoryTarget): HistoryView;
  /** A screen shows this series: read it from the device, then bring it up to date. Returns the release. */
  watch(target: HistoryTarget, fn: (view: HistoryView) => void): () => void;
  more(target: HistoryTarget): Promise<void>;
  detail(actionId: string): DetailView | null;
  /** Reads an action's detail, kept or from the server, or its next page of changes. */
  loadDetail(actionId: string, more?: boolean): Promise<void>;
  onDetail(actionId: string, fn: (view: DetailView) => void): () => void;
  /** What a reply said of the stamp, after the mirror stored it. */
  absorb(stamp: ActivityStamp | null): void;
  /** The queue moved: this device's own lines may have changed. */
  queueMoved(): void;
  /** Whole actions the server refused, as they were queued, with what it said. */
  refused(lines: readonly { record: Pending; reason?: string }[]): void;
  /** Drops everything kept, on the device and here: another account is taking the device. */
  forget(): Promise<void>;
  close(): void;
}

/** How many unfinished groups a refresh reads again, so a page of them costs a bounded number of reads. */
const GROUP_REREADS = 5;
/** How many refused lines are kept, in memory only: each was announced as a problem when it happened. */
const REFUSED_KEPT = 50;

interface Entry {
  target: HistoryTarget;
  series: SeriesRow | null;
  hydrated: boolean;
  status: HistoryStatus;
  failure: HistoryFailure | null;
  refreshing: boolean;
  again: boolean;
  loadingMore: boolean;
  watchers: Set<(view: HistoryView) => void>;
  local: LocalLine[];
  /** Whether this series' unfinished groups were read again since the app opened, or since names changed. */
  groupsChecked: boolean;
}

export function failureOf(error: unknown): HistoryFailure {
  if (error instanceof ApiError) {
    if (error.offline) return "offline";
    if (error.status === 401) return "signed-out";
    if (error.status === 404) return "not_found";
  }
  return "error";
}

/** Whether a queued or refused line belongs to a target's history. */
export function concerns(target: HistoryTarget, line: { entity: string; id: string; listId: string | null; before: Pending["before"] }): boolean {
  if (target.kind === "account") return true;
  if (target.kind === "item") return line.entity === "item" && line.id === target.itemId;
  if (line.entity === "list") return line.id === target.listId;
  const left = line.before && "listId" in line.before ? line.before.listId : null;
  return line.listId === target.listId || left === target.listId;
}

export function createHistory(deps: {
  db: IDBDatabase;
  wire: HistoryWire | undefined;
  stamp: ActivityStamp | null;
  pending: () => Promise<Pending[]>;
  /** Asks for one sync, so the stamp is fresh when a series is opened. */
  sync: () => void;
  now?: () => number;
}): HistoryControl {
  const now = deps.now ?? Date.now;
  let stamp = deps.wire ? deps.stamp : null;
  let closed = false;
  const entries = new Map<string, Entry>();
  const availableWatchers = new Set<(available: boolean) => void>();
  const details = new Map<string, DetailView>();
  const detailWatchers = new Map<string, Set<(view: DetailView) => void>>();
  let refusedLines: LocalLine[] = [];

  const available = () => stamp !== null;

  function entry(target: HistoryTarget): Entry {
    const key = targetKey(target);
    let found = entries.get(key);
    if (!found) {
      found = { target, series: null, hydrated: false, status: available() ? "loading" : "unavailable", failure: null, refreshing: false,
        again: false, loadingMore: false, watchers: new Set(), local: [], groupsChecked: false };
      entries.set(key, found);
    }
    return found;
  }

  function view(target: HistoryTarget): HistoryView {
    const found = entry(target);
    const series = found.series;
    const held = new Set(series?.actions.map((action) => action.operationId) ?? []);
    return {
      status: !available() ? "unavailable" : series ? "ready" : found.status,
      actions: series?.actions ?? [],
      more: Boolean(series?.next),
      loadingMore: found.loadingMore,
      refreshing: found.refreshing,
      coverage: series?.coverage ?? null,
      readAt: series?.readAt ?? null,
      current: series !== null && current(series, stamp),
      failure: series ? found.failure : null,
      local: found.local.filter((line) => line.state === "refused" || line.operationId === null || !held.has(line.operationId)),
    };
  }

  function tell(found: Entry): void {
    if (closed) return;
    const shown = view(found.target);
    for (const watcher of found.watchers) watcher(shown);
  }

  async function keep(series: SeriesRow, epoch: string): Promise<void> {
    await keepHistory(deps.db, epoch, { series: bounded(series) }).catch(() => false);
  }

  /** Reads a series' head down to what it holds, or its first page when it holds nothing read under this epoch. */
  async function refresh(found: Entry): Promise<void> {
    const wire = deps.wire;
    if (!wire || !stamp || closed) return;
    if (found.refreshing) { found.again = true; return; }
    found.refreshing = true;
    tell(found);
    const at = stamp;
    const query = targetQuery(found.target);
    try {
      let series = found.series;
      const moved = !series || series.epoch !== at.epoch || series.head !== at.head;
      if (!series || series.epoch !== at.epoch) {
        series = freshSeries(found.target, at, await wire.page(query), now());
      } else if (series.head !== at.head) {
        const pages: ActionSummary[] = [];
        const marks: SeriesRow["marks"] = [];
        let coverage: readonly CoverageView[] | undefined;
        let cursor: string | undefined;
        let met = false;
        for (let turn = 0; turn < HEAD_READ_PAGES; turn++) {
          const page = await wire.page({ ...query, ...(cursor ? { cursor } : {}) });
          if (turn === 0) coverage = page.coverage;
          pages.push(...page.actions);
          const last = page.actions[page.actions.length - 1];
          if (last) marks.push({ after: last.id, next: page.next });
          if (meet(series.actions, pages).met) { met = true; break; }
          if (!page.next) break;
          cursor = page.next;
        }
        series = advance(series, at, pages, met, marks, coverage, now());
      } else {
        series = { ...series, lastUsed: now() };
      }
      // A group still open is provisional. Its counts are read again when history moved, and once a session
      // otherwise, since a group can close without a new action. One that cannot be read keeps what the page said.
      const checking = moved || !found.groupsChecked;
      if (checking) {
        const open = series.actions.filter((action) => action.group && !action.group.finished).slice(0, GROUP_REREADS);
        for (const action of open) {
          try { series = replaced(series, (await wire.action(action.id)).action); } catch { /* read again on the next move */ }
        }
      }
      if (closed || !stamp || stamp.epoch !== at.epoch) return;
      found.series = series;
      if (checking) found.groupsChecked = true;
      found.failure = null;
      found.status = "ready";
      await keep(series, at.epoch);
    } catch (error) {
      const failure = failureOf(error);
      found.failure = failure;
      if (!found.series) found.status = failure;
    } finally {
      found.refreshing = false;
      tell(found);
      if (found.again && !closed) { found.again = false; void refresh(found); }
    }
  }

  async function hydrate(found: Entry): Promise<void> {
    if (found.hydrated) return;
    found.hydrated = true;
    const kept = await readHistory(deps.db, found.target).catch(() => ({ stamp: null, series: null }));
    if (kept.series && stamp && kept.series.epoch === stamp.epoch && !found.series) found.series = kept.series;
  }

  async function local(found: Entry): Promise<void> {
    const records = await deps.pending().catch(() => [] as Pending[]);
    const waiting: LocalLine[] = records.filter((record) => !record.unsent && concerns(found.target, record)).map((record) => ({
      state: "waiting", operationId: record.operationId ?? null, op: record.op, entity: record.entity, id: record.id,
      listId: record.listId, fields: record.fields, before: record.before, occurredAt: record.occurredAt ?? null,
    }));
    found.local = [...waiting, ...refusedLines.filter((line) => concerns(found.target, line))];
  }

  async function open(found: Entry): Promise<void> {
    await hydrate(found);
    await local(found);
    tell(found);
    if (!available()) return;
    deps.sync();
    await refresh(found);
  }

  function setDetail(actionId: string, next: DetailView): void {
    details.set(actionId, next);
    if (closed) return;
    for (const watcher of detailWatchers.get(actionId) ?? []) watcher(next);
  }

  function drop(): void {
    for (const found of entries.values()) {
      found.series = null;
      found.groupsChecked = false;
      found.failure = null;
      found.status = available() ? "loading" : "unavailable";
    }
    for (const id of [...details.keys()]) details.delete(id);
  }

  return {
    available,
    onAvailable(fn) {
      availableWatchers.add(fn);
      return () => availableWatchers.delete(fn);
    },
    view,
    watch(target, fn) {
      const found = entry(target);
      found.watchers.add(fn);
      void open(found);
      return () => { found.watchers.delete(fn); };
    },
    async more(target) {
      const found = entry(target);
      const series = found.series;
      const wire = deps.wire;
      if (!wire || !stamp || !series?.next || found.loadingMore) return;
      found.loadingMore = true;
      tell(found);
      const at = stamp;
      try {
        const page = await wire.page({ ...targetQuery(target), cursor: series.next });
        if (!stamp || stamp.epoch !== at.epoch || found.series !== series) return;
        found.series = extend(series, page, now());
        found.failure = null;
        await keep(found.series, at.epoch);
      } catch (error) {
        found.failure = failureOf(error);
      } finally {
        found.loadingMore = false;
        tell(found);
      }
    },
    detail: (actionId) => details.get(actionId) ?? null,
    async loadDetail(actionId, more = false) {
      const wire = deps.wire;
      if (!wire || !stamp) return;
      const held = details.get(actionId);
      if (held?.loadingMore || (held?.status === "loading")) return;
      const at = stamp;
      if (!more && held?.status === "ready") return;
      if (!more && !held) {
        const kept = await readDetail(deps.db, actionId).catch(() => null);
        if (kept && kept.epoch === at.epoch) { setDetail(actionId, { status: "ready", view: kept.view, loadingMore: false, failure: null }); return; }
        // Asked twice at once, the first to get here reads it, and the second leaves it to the first.
        if (details.get(actionId)) return;
      }
      const base = held?.view ?? null;
      setDetail(actionId, more && base ? { ...held!, loadingMore: true, failure: null } : { status: "loading", view: null, loadingMore: false, failure: null });
      try {
        const cursor = more ? base?.changes.next ?? undefined : undefined;
        const page = await wire.action(actionId, cursor);
        if (!stamp || stamp.epoch !== at.epoch) return;
        const view: ActionDetailView = more && base
          ? { ...page, changes: { total: page.changes.total, items: [...base.changes.items, ...page.changes.items], next: page.changes.next } }
          : page;
        setDetail(actionId, { status: "ready", view, loadingMore: false, failure: null });
        const time = now();
        await keepHistory(deps.db, at.epoch, { detail: { v: 1, actionId, epoch: at.epoch, view, readAt: time, lastUsed: time, bytes: detailBytes(view) } })
          .catch(() => false);
      } catch (error) {
        const failure = failureOf(error);
        setDetail(actionId, base ? { status: "ready", view: base, loadingMore: false, failure } : { status: failure, view: null, loadingMore: false, failure });
      }
    },
    onDetail(actionId, fn) {
      const set = detailWatchers.get(actionId) ?? new Set();
      set.add(fn);
      detailWatchers.set(actionId, set);
      return () => { set.delete(fn); if (!set.size) detailWatchers.delete(actionId); };
    },
    absorb(next) {
      if (!deps.wire || closed) return;
      const was = stamp;
      stamp = next;
      if (!next || (was && was.epoch !== next.epoch)) drop();
      if ((was === null) !== (next === null)) for (const watcher of availableWatchers) watcher(next !== null);
      for (const found of entries.values()) {
        if (!found.watchers.size) continue;
        tell(found);
        if (next) void refresh(found);
      }
    },
    queueMoved() {
      for (const found of entries.values()) {
        if (!found.watchers.size) continue;
        void local(found).then(() => tell(found));
      }
    },
    refused(lines) {
      for (const { record, reason } of lines) {
        refusedLines = [{
          state: "refused" as const, operationId: record.operationId ?? null, op: record.op, entity: record.entity, id: record.id, listId: record.listId,
          fields: record.fields, before: record.before, occurredAt: record.occurredAt ?? null, ...(reason ? { reason } : {}),
        }, ...refusedLines].slice(0, REFUSED_KEPT);
      }
    },
    async forget() {
      drop();
      for (const found of entries.values()) found.hydrated = false;
      refusedLines = [];
      await forgetHistory(deps.db);
    },
    close() {
      closed = true;
      entries.clear();
      details.clear();
      detailWatchers.clear();
      availableWatchers.clear();
    },
  };
}
